/**
 * TriServe Auth Service
 * Helpers for acquiring and storing JWT tokens.
 * Refresh token is managed server-side via HttpOnly cookie — never stored in JS storage.
 */

import { API_CONFIG, AUTH_CONFIG, DEBUG_CONFIG } from '../config';
import { AUTH_ROUTES, getTriServeApiBaseUrl, joinApiUrl } from '../utils/apiRoutes';

const JWT_STORAGE_KEY = 'tse.jwt';
const REFRESH_EXPIRY_STORAGE_KEY = 'tse.refreshTokenExpiryUtc';
const DARK_MODE_STORAGE_KEY = 'tse.darkModeDefault';

// Legacy key — cleared on startup for backward compat
const LEGACY_REFRESH_TOKEN_KEY = 'tse.refreshToken';

const storage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage || null;
};

const legacyStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage || null;
};

/**
 * Builds the common headers required by every TriServe Auth API call.
 * Both Bearer (JWT) and X-TriServe-App-Key are in the global security requirement.
 */
const buildAuthHeaders = (extraHeaders = {}) => ({
  'X-TriServe-App-Key': API_CONFIG.AUTH_APP_KEY || 'triserve-energy',
  'Accept': 'application/json',
  ...extraHeaders
});

const normalizeTokenResponse = (json) => {
  const payload = json?.data ?? json;
  const token = payload?.token ?? null;
  // refreshToken is NOT returned in the body — it lives in an HttpOnly cookie.
  const refreshTokenExpiryTime = payload?.refreshTokenExpiryTime ?? null;
  const darkModeDefault = typeof payload?.darkModeDefault === 'boolean' ? payload.darkModeDefault : null;

  return { token, refreshTokenExpiryTime, darkModeDefault };
};

const getErrorDetail = (json, fallbackText) => {
  if (Array.isArray(json?.messages) && json.messages.length > 0) {
    return json.messages.join(', ');
  }

  const payload = json?.data ?? json;
  return payload?.message || payload?.error || json?.message || json?.error || json?.title || fallbackText;
};

const getLoginNetworkErrorMessage = (requestUrl, error) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'the current browser origin';
  const detail = error?.message ? ` (${error.message})` : '';

  return `Unable to reach the authentication service at ${requestUrl}${detail}. Confirm the hostname resolves, the API is running, and the Auth API CORS configuration allows ${origin}.`;
};

// ---------------------------------------------------------------------------
// Token storage helpers
// ---------------------------------------------------------------------------

export const getJwt = () => {
  const s = storage();
  if (!s) return null;
  return s.getItem(JWT_STORAGE_KEY);
};

export const getDarkModeDefault = () => {
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(DARK_MODE_STORAGE_KEY);
  if (raw === null) return null;
  return raw === 'true';
};

export const setJwt = ({ token, refreshTokenExpiryTime, darkModeDefault } = {}) => {
  const s = storage();
  if (!s) return;
  if (token) s.setItem(JWT_STORAGE_KEY, token);
  if (refreshTokenExpiryTime) s.setItem(REFRESH_EXPIRY_STORAGE_KEY, refreshTokenExpiryTime);
  if (darkModeDefault !== null && darkModeDefault !== undefined) {
    s.setItem(DARK_MODE_STORAGE_KEY, String(darkModeDefault));
  }
};

export const clearJwt = () => {
  const s = storage();
  if (s) {
    s.removeItem(JWT_STORAGE_KEY);
    s.removeItem(REFRESH_EXPIRY_STORAGE_KEY);
    s.removeItem(DARK_MODE_STORAGE_KEY);
    // Clear legacy refresh token key if present
    s.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  }

  const legacy = legacyStorage();
  if (legacy) {
    legacy.removeItem(JWT_STORAGE_KEY);
    legacy.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    legacy.removeItem(REFRESH_EXPIRY_STORAGE_KEY);
    legacy.removeItem(DARK_MODE_STORAGE_KEY);
  }
};

// ---------------------------------------------------------------------------
// Auth API calls
// ---------------------------------------------------------------------------

export const login = async ({ email, password } = {}) => {
  const baseUrl = getTriServeApiBaseUrl();
  const requestUrl = AUTH_ROUTES.login();
  const requestInit = {
    method: 'POST',
    credentials: 'include', // required for HttpOnly refresh-token cookie
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ email, password })
  };

  const attempt = async (url) => {
    const response = await fetch(url, requestInit);
    const text = await response.text();
    return { response, text };
  };

  let response;
  let text;
  try {
    ({ response, text } = await attempt(requestUrl));
  } catch (e) {
    // Common dev setup: ASP.NET runs HTTPS on localhost and will close the connection
    // if contacted over HTTP, which surfaces as a generic fetch failure.
    if (typeof baseUrl === 'string' && baseUrl.startsWith('http://localhost')) {
      const httpsBase = baseUrl.replace(/^http:\/\//, 'https://');
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.warn('Login fetch failed; retrying over HTTPS:', httpsBase);
      }
      try {
        ({ response, text } = await attempt(joinApiUrl(httpsBase, API_CONFIG.AUTH_LOGIN_ENDPOINT || '/api/v1/auth/login')));
      } catch (retryError) {
        if (DEBUG_CONFIG.LOG_API_CALLS) {
          console.warn('Login fetch failed after HTTPS retry:', retryError);
        }
        throw new Error(getLoginNetworkErrorMessage(joinApiUrl(httpsBase, API_CONFIG.AUTH_LOGIN_ENDPOINT || '/api/v1/auth/login'), retryError));
      }
    } else {
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.warn('Login fetch failed:', e);
      }
      throw new Error(getLoginNetworkErrorMessage(requestUrl, e));
    }
  }

  let json;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const detail = text
      ? getErrorDetail(json, response.statusText || 'Request failed')
      : (response.statusText || 'Request failed');
    throw new Error(`Login failed: ${response.status} ${response.statusText} - ${detail}`);
  }

  const tokens = normalizeTokenResponse(json);
  if (!tokens.token) {
    throw new Error('Login succeeded but no token was returned');
  }

  setJwt(tokens);

  if (DEBUG_CONFIG.LOG_API_CALLS) {
    console.log('Auth login succeeded; JWT stored');
  }

  return tokens;
};

/**
 * Calls the logout endpoint to clear the server-side HttpOnly refresh-token cookie,
 * then clears all local JWT storage.
 */
export const logout = async () => {
  const requestUrl = AUTH_ROUTES.logout();
  const jwt = getJwt();

  try {
    const headers = buildAuthHeaders();
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;

    await fetch(requestUrl, {
      method: 'POST',
      credentials: 'include',
      headers
    });
  } catch (e) {
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.warn('Logout API call failed (token cleared locally anyway):', e);
    }
  } finally {
    clearJwt();
  }
};

/**
 * Self-registers a new SA user account via /api/v1/auth/self-register.
 * Fields: firstName, lastName, email, password, lanId (optional), phoneNumber (optional).
 */
export const selfRegister = async ({ firstName, lastName, email, password, lanId, phoneNumber } = {}) => {
  if (!AUTH_CONFIG.ENABLE_REGISTRATION) {
    throw new Error('Registration is disabled for this environment.');
  }

  const requestUrl = AUTH_ROUTES.selfRegister();

  let response;
  let text;
  try {
    response = await fetch(requestUrl, {
      method: 'POST',
      credentials: 'include',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ firstName, lastName, email, password, lanId, phoneNumber })
    });
    text = await response.text();
  } catch (e) {
    throw new Error(getLoginNetworkErrorMessage(requestUrl, e));
  }

  let json;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const detail = text
      ? getErrorDetail(json, response.statusText || 'Registration failed')
      : (response.statusText || 'Registration failed');
    throw new Error(`Registration failed: ${response.status} ${response.statusText} - ${detail}`);
  }

  if (DEBUG_CONFIG.LOG_API_CALLS) {
    console.log('Self-registration succeeded');
  }

  // StringResponse — data is a success message string, not a token
  return { succeeded: json?.succeeded ?? true, message: json?.data ?? null };
};

/** @deprecated Use selfRegister directly */
export const register = async (fields = {}) => selfRegister(fields);

/**
 * Refreshes the access token using the HttpOnly refresh-token cookie.
 * Returns the new token payload on success or null if the session has expired.
 */
export const refreshAccessToken = async () => {
  const requestUrl = AUTH_ROUTES.refreshToken();

  let response;
  let text;
  try {
    response = await fetch(requestUrl, {
      method: 'POST',
      credentials: 'include', // sends the HttpOnly refresh-token cookie
      headers: buildAuthHeaders()
    });
    text = await response.text();
  } catch (e) {
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.warn('Token refresh request failed:', e);
    }
    return null;
  }

  if (!response.ok) {
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.warn('Token refresh failed:', response.status, response.statusText);
    }
    return null;
  }

  let json;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  const tokens = normalizeTokenResponse(json);
  if (!tokens.token) {
    return null;
  }

  setJwt(tokens);

  if (DEBUG_CONFIG.LOG_API_CALLS) {
    console.log('Token refresh succeeded; new JWT stored');
  }

  return tokens;
};

export const verifyJwt = async (token = getJwt()) => {
  if (!token) {
    return false;
  }

  const baseUrl = getTriServeApiBaseUrl();
  if (!baseUrl) {
    return true;
  }

  const requestUrl = AUTH_ROUTES.verify();

  try {
    const response = await fetch(requestUrl, {
      method: 'GET',
      credentials: 'include',
      headers: buildAuthHeaders({ 'Authorization': `Bearer ${token}` })
    });

    if (!response.ok) {
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.warn('JWT verification failed:', response.status, response.statusText);
      }
      return false;
    }

    return true;
  } catch (error) {
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.warn('JWT verification request failed:', error);
    }
    return false;
  }
};
